// ============================================================================
//  courtDims — wymiarowanie widoku 2D (jak na layoutach produkcyjnych Epufloor):
//  linie wymiarowe wokół płyty z etykietami „X,XX m · N szt".
//  dimsSpec to CZYSTA funkcja (bez THREE/DOM) — testowalna pod Node; render
//  (kreski + „wąsy" + sprite'y tekstu) buduje Court z tej specyfikacji.
//  Układ świata: XZ, linia końcowa na Z=0, boisko rośnie w +Z (jak Court).
// ============================================================================

const fmtM = (m) => `${m.toFixed(2)} m`

// Etykieta „metry · sztuki"; sztuki tylko gdy wymiar to PEŁNE płytki (płyta z
// zapleczem masztu > wybieg nie jest pełną wielokrotnością — nie zmyślamy sztuk).
function labelFor(meters, tileM) {
  const tiles = meters / tileM
  const rounded = Math.round(tiles)
  return Math.abs(tiles - rounded) < 0.01 ? `${fmtM(meters)} · ${rounded} szt` : fmtM(meters)
}

// Etykieta wymiaru ZEWNĘTRZNEGO (z obrzeżem): same metry z dopiskiem — obrzeże
// nie jest płytką, więc sztuk nie podajemy (pole gry i wybieg mają swoje).
// Bez obrzeża (edgeM = 0) zostaje zwykła etykieta płyty „metry · sztuki".
function outerLabel(meters, tileM, edgeM) {
  return edgeM > 0 ? `${fmtM(meters)} z obrzeżem` : labelFor(meters, tileM)
}

// Specyfikacja linii wymiarowych: [{ x1, z1, x2, z2, label }] w metrach świata.
// Wymiary: boisko ZEWNĘTRZNIE z obrzeżem (szer. u góry, dł. z lewej — uwaga
// klienta 04.09.2026: wymiary zawsze z obrzeżem, jak na layoutach), pole gry
// (szer. u dołu, dł. z prawej), wybieg (odcinek od wewnętrznej krawędzi
// obrzeża do pola gry na dolnej linii — same płytki), trumna (szer./głęb.
// tuż przy niej — tylko w sztukach, jak na layoutach).
//   widthTiles/lengthTiles — pole gry [szt]; runoffTiles — wybieg [szt];
//   keyWTiles/keyLTiles — trumna [szt] (0 = brak); tileM — płytka [m];
//   fullW/fullL/centerZ — płyta z płytek (z plateExtent) [m];
//   edgeM — obrzeże [m], leży NA ZEWNĄTRZ płyty (wymiar zewn. full + 2·edge)
export function dimsSpec({ widthTiles, lengthTiles, runoffTiles, keyWTiles, keyLTiles, tileM, fullW, fullL, centerZ, edgeM = 0 }) {
  const outerW = fullW + 2 * edgeM
  const outerL = fullL + 2 * edgeM
  const off = Math.max(0.5, outerW * 0.06)
  const zMin = centerZ - outerL / 2
  const zMax = centerZ + outerL / 2
  const halfW = outerW / 2
  const courtW = widthTiles * tileM
  const courtL = lengthTiles * tileM
  const dims = []

  // Boisko zewnętrznie z obrzeżem: szerokość (za koszem, Z = zMin - off) i długość (z lewej).
  dims.push({ x1: -halfW, z1: zMin - off, x2: halfW, z2: zMin - off, label: outerLabel(outerW, tileM, edgeM) })
  dims.push({ x1: -halfW - off, z1: zMin, x2: -halfW - off, z2: zMax, label: outerLabel(outerL, tileM, edgeM) })

  // Pole gry: szerokość (przed boiskiem) i długość (z prawej).
  dims.push({ x1: -courtW / 2, z1: zMax + off, x2: courtW / 2, z2: zMax + off, label: labelFor(courtW, tileM) })
  dims.push({ x1: halfW + off, z1: 0, x2: halfW + off, z2: courtL, label: labelFor(courtL, tileM) })

  // Wybieg: od wewnętrznej krawędzi obrzeża (brzeg płytek) do pola gry —
  // na dolnej linii wymiarowej; liczony w płytkach, bez obrzeża.
  if (runoffTiles > 0) {
    dims.push({ x1: -fullW / 2, z1: zMax + off, x2: -courtW / 2, z2: zMax + off, label: `${runoffTiles} szt` })
  }

  // Trumna: szerokość (pod linią rzutów wolnych) i głębokość (przy prawym brzegu).
  if (keyWTiles > 0 && keyLTiles > 0) {
    const keyW = keyWTiles * tileM
    const keyL = keyLTiles * tileM
    dims.push({ x1: -keyW / 2, z1: keyL + off * 0.5, x2: keyW / 2, z2: keyL + off * 0.5, label: `${keyWTiles} szt` })
    dims.push({ x1: keyW / 2 + off * 0.5, z1: 0, x2: keyW / 2 + off * 0.5, z2: keyL, label: `${keyLTiles} szt` })
  }

  return dims
}
